/**
 * Classification Metrics Cards Component
 *
 * Displays KPI summary cards for document classification metrics
 */

import { MetricCard } from './MetricCard';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import { formatNumber, formatLatency } from '@/lib/formatters';
import type { ClassificationSummary } from '@/types/dashboard';

interface ClassificationMetricsCardsProps {
  data: ClassificationSummary | null | undefined;
  isLoading: boolean;
}

export function ClassificationMetricsCards({
  data,
  isLoading,
}: ClassificationMetricsCardsProps) {
  // Show skeleton when loading OR when data hasn't arrived yet
  const showSkeleton = isLoading || data === null || data === undefined;

  return (
    <div
      className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      role="region"
      aria-label="Document classification metrics summary"
    >
      <MetricCard
        title="Classifications"
        value={formatNumber(data?.totalCompleted)}
        description="Documents classified in period"
        isLoading={showSkeleton}
        variant="default"
        tooltip={METRIC_DESCRIPTIONS.classificationsCompleted}
      />
      <MetricCard
        title="Avg Latency"
        value={formatLatency(data?.avgLatencyMs)}
        description="Average classification time"
        isLoading={showSkeleton}
        threshold={
          data?.avgLatencyMs !== undefined
            ? {
                value: data.avgLatencyMs,
                warningThreshold: 500,
                criticalThreshold: 2000,
                inverse: true,
              }
            : undefined
        }
        tooltip={METRIC_DESCRIPTIONS.classificationLatency}
      />
      <MetricCard
        title="Pending Review"
        value={formatNumber(data?.pendingReview)}
        description="Documents needing manual review"
        isLoading={showSkeleton}
        threshold={
          data?.pendingReview !== undefined
            ? {
                value: data.pendingReview,
                warningThreshold: 5,
                criticalThreshold: 20,
                inverse: true,
              }
            : undefined
        }
        tooltip={METRIC_DESCRIPTIONS.pendingReview}
      />
      <MetricCard
        title="Errors"
        value={formatNumber(data?.totalErrors)}
        description="Classification failures"
        isLoading={showSkeleton}
        threshold={
          data?.totalErrors !== undefined
            ? {
                value: data.totalErrors,
                warningThreshold: 1,
                criticalThreshold: 10,
                inverse: true,
              }
            : undefined
        }
        tooltip={METRIC_DESCRIPTIONS.classificationErrors}
      />
    </div>
  );
}
